/**
 * SocialProofRotator — single-line rotating quote block under the Coherence
 * explainer on the landing page hero.
 *
 * ⚠️  ⚠️  ⚠️  IMPORTANT — REPLACE THESE BEFORE LAUNCHING TO PAID TRAFFIC ⚠️  ⚠️  ⚠️
 *
 * The QUOTES array below ships with three default *use-case-framed* lines.
 * They are deliberately NOT attributed to fictitious people (no "— John D.,
 * NYC" cosplay) because fake testimonials are an advertising-law and brand-
 * trust minefield. They read as plausible visitor pull-quotes rather than
 * as named reviews.
 *
 * Once you have 3 real, attributable testimonials (with permission to use
 * them), swap them in by editing the QUOTES array. Each entry takes:
 *   - text:    the quote itself, no surrounding quotation marks needed
 *   - context: a short tag rendered after an em-dash (e.g. "Long-haul
 *              traveller", "Daily user, focus"). Keep ≤ 28 chars so the
 *              line still fits a single-line desktop layout.
 *
 * Behaviour:
 *   - Quotes rotate every 6s with a 600ms cross-fade.
 *   - Pauses on hover so visitors can read at their own pace.
 *   - Pauses + freezes the visible quote when prefers-reduced-motion is set.
 *   - Pure React + Tailwind. No backend, no fonts, no extra deps.
 */
import React, { useEffect, useRef, useState } from 'react';

const QUOTES = [
  // TODO: replace with real testimonials before paid launch.
  {
    text: 'Used it on a 14-hour flight to Tokyo. Best sleep I\u2019ve had in years.',
    context: 'Long-haul traveller',
  },
  {
    text: 'First app where the heartbeat feels alive instead of looped. The breath sync is unreal.',
    context: 'Coherence Mode user',
  },
  {
    text: 'Replaced my paid focus subscription on day one.',
    context: 'Daily user, focus',
  },
];

const ROTATION_MS = 6000;
const FADE_MS = 600;

export const SocialProofRotator = () => {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  const pausedRef = useRef(false);

  useEffect(() => {
    // Honour reduced-motion: freeze on the first quote, no rotation.
    if (typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const tick = () => {
      if (pausedRef.current) return;
      // Fade out → swap → fade in. Keeps both states (DOM-stable single
      // <p>) so screen readers don't get spammed by mount/unmount churn.
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % QUOTES.length);
        setVisible(true);
      }, FADE_MS);
    };

    const id = setInterval(tick, ROTATION_MS);
    return () => clearInterval(id);
  }, []);

  const q = QUOTES[idx];

  return (
    <div
      className="flex justify-center mb-12"
      data-testid="social-proof-root"
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
    >
      <div
        className="max-w-2xl text-center px-4"
        aria-live="polite"
      >
        <p
          data-testid="social-proof-quote"
          className={`text-white/55 text-sm sm:text-base italic leading-relaxed transition-opacity duration-500 ${
            visible ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ transitionDuration: `${FADE_MS}ms` }}
        >
          <span className="text-white/30 mr-1">&ldquo;</span>
          {q.text}
          <span className="text-white/30 ml-1">&rdquo;</span>
          <span className="block sm:inline text-white/30 not-italic text-xs sm:text-sm sm:ml-3 mt-1 sm:mt-0">
            &mdash; {q.context}
          </span>
        </p>
      </div>
    </div>
  );
};

export default SocialProofRotator;
